import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { DollarSign, Plus, Edit } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["Electricity", "Fuel", "Raw Material Transport", "Labour", "Maintenance", "Equipment", "Packaging", "Administration", "Safety", "Other"];

const FactoryExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [unitFilter, setUnitFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [form, setForm] = useState({ category: "Electricity", description: "", amount: "", unit: "Unit 1", date: new Date().toISOString().split("T")[0] });

  const fetchExpenses = async () => {
    try {
      let params = "?";
      if (unitFilter !== "all") params += `unit=${unitFilter}&`;
      if (catFilter !== "all") params += `category=${catFilter}&`;
      const res = await authFetch(`${API}/factory/expenses${params}`);
      const data = await res.json();
      if (data.success) {
        setExpenses(data.data || []);
        setTotalAmount(data.meta?.total_amount || 0);
      }
    } catch { toast.error("Failed to load expenses"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchExpenses(); }, [unitFilter, catFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const body = { ...form, amount: parseFloat(form.amount) };
      const res = await authFetch(`${API}/factory/expenses`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Expense recorded");
        setShowAdd(false);
        setForm({ category: "Electricity", description: "", amount: "", unit: "Unit 1", date: new Date().toISOString().split("T")[0] });
        fetchExpenses();
      } else toast.error(data.detail || "Failed");
    } catch { toast.error("Network error"); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-6" data-testid="factory-expenses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Sora']">Factory Expenses</h1>
          <p className="text-emerald-600 text-sm mt-1">Track and manage factory expenditure</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-expense-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Expense
        </Button>
      </div>

      <div className="flex gap-3 items-center">
        <Select value={unitFilter} onValueChange={setUnitFilter}>
          <SelectTrigger className="w-36 bg-[#1C2B2B] border-emerald-900/50 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Units</SelectItem>
            <SelectItem value="Unit 1">Unit 1</SelectItem>
            <SelectItem value="Unit 2">Unit 2</SelectItem>
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48 bg-[#1C2B2B] border-emerald-900/50 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-right">
          <p className="text-xs text-emerald-600">Total</p>
          <p className="text-lg font-bold text-emerald-400 font-mono">Rs. {totalAmount.toLocaleString("en-IN")}</p>
        </div>
      </div>

      <Card className="bg-[#1C2B2B] border-emerald-900/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead>
              <tr className="border-b border-emerald-900/30">
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Date</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Category</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Description</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Unit</th>
                <th className="text-right p-3 text-emerald-600 text-xs font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></td></tr>
              ) : expenses.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-slate-500">No expenses recorded yet</td></tr>
              ) : expenses.map((exp, i) => (
                <tr key={exp.expense_id || i} className="border-b border-emerald-900/20 hover:bg-emerald-900/10" data-testid={`expense-row-${i}`}>
                  <td className="p-3 text-slate-300 text-xs">{exp.date}</td>
                  <td className="p-3"><Badge className="bg-emerald-900/30 text-emerald-400 text-[10px]">{exp.category}</Badge></td>
                  <td className="p-3 text-white">{exp.description}</td>
                  <td className="p-3"><Badge variant="outline" className="text-[10px] border-emerald-700 text-emerald-400">{exp.unit || "-"}</Badge></td>
                  <td className="p-3 text-right text-emerald-400 font-mono font-medium">Rs. {(exp.amount || 0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md bg-[#1A2332] border-emerald-900/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-['Sora']">Add Expense</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-emerald-600 font-medium">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Unit</label>
                <Select value={form.unit} onValueChange={(v) => setForm({...form, unit: v})}>
                  <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Unit 1">Unit 1</SelectItem><SelectItem value="Unit 2">Unit 2</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-emerald-600 font-medium">Description</label>
              <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="expense-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-emerald-600 font-medium">Amount (Rs.)</label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} required className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="expense-amount" />
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Date</label>
                <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="expense-date" />
              </div>
            </div>
            <Button type="submit" disabled={creating} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-expense-btn">
              {creating ? "Saving..." : "Add Expense"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FactoryExpensesPage;
